import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import toast from 'react-hot-toast';
import useAuthStore from '../stores/authStore';
import { Button, Input } from '../components/ui';

const loginSchema = z.object({
	email: z.string().email('Invalid email address'),
	password: z.string().min(6, 'Password must be at least 6 characters'),
	rememberMe: z.boolean().optional(),
});

type LoginFormData = z.infer<typeof loginSchema>;

const Login = () => {
	const navigate = useNavigate();
	const { login } = useAuthStore();
	const [showPassword, setShowPassword] = useState(false);

	const {
		register,
		handleSubmit,
		formState: { errors, isSubmitting },
	} = useForm<LoginFormData>({
		resolver: zodResolver(loginSchema),
		defaultValues: {
			rememberMe: false,
		},
	});

	const onSubmit = async (data: LoginFormData) => {
		const loginPromise = login(data.email, data.password);

		toast.promise(loginPromise, {
			loading: 'Signing in...',
			success: 'Login successful!',
			error: 'Invalid email or password',
		});

		const success = await loginPromise;

		if (success) {
			setTimeout(() => {
				navigate('/dashboard');
			}, 500);
		}
	};

	return (
		<div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-600 via-blue-600 to-purple-700 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 transition-colors">
			<div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-md p-12 m-5 transition-colors">
				<div className="text-center mb-8">
					<h1 className="text-3xl font-bold bg-gradient-to-r from-purple-600 to-purple-800 dark:from-purple-400 dark:to-purple-600 bg-clip-text text-transparent mb-2">
						Beauty Business Portal
					</h1>
					<p className="text-sm text-gray-600 dark:text-gray-400">Sign in to your account</p>
				</div>

				<form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
					<Input
						type="email"
						label="Email Address"
						placeholder="Enter your email"
						error={errors.email?.message}
						autoComplete="email"
						{...register('email')}
					/>

					<div className="relative">
						<Input
							type={showPassword ? 'text' : 'password'}
							label="Password"
							placeholder="Enter your password"
							error={errors.password?.message}
							autoComplete="current-password"
							{...register('password')}
						/>
						<button
							type="button"
							className="absolute right-3 top-9 text-gray-400 dark:text-gray-500 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
							onClick={() => setShowPassword(!showPassword)}
						>
							{showPassword ? '👁️' : '👁️‍🗨️'}
						</button>
					</div>

					<div className="flex items-center justify-between">
						<label className="flex items-center gap-2 cursor-pointer">
							<input type="checkbox" className="w-4 h-4 cursor-pointer rounded" {...register('rememberMe')} />
							<span className="text-sm text-gray-700 dark:text-gray-300">Remember me</span>
						</label>
						<a href="#" className="text-sm text-blue-600 dark:text-blue-400 hover:underline font-semibold">
							Forgot password?
						</a>
					</div>

					<Button type="submit" variant="primary" className="w-full" size="lg" isLoading={isSubmitting}>
						Sign In
					</Button>
				</form>

				<div className="mt-8 pt-6 border-t border-gray-200 dark:border-gray-700 text-center">
					<a href="#" className="text-blue-600 dark:text-blue-400 font-semibold text-sm hover:underline">
						Need help signing in?
					</a>
				</div>

				<div className="mt-6 text-center text-xs text-gray-500 dark:text-gray-400">© 2025 Beauty Business Portal. All rights reserved.</div>

				{/* Development Credentials Helper */}
				{/* <div className="mt-6 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800 transition-colors">
					<p className="text-xs font-semibold text-blue-900 dark:text-blue-300 mb-2">Test Credentials:</p>
					<div className="text-xs text-blue-800 dark:text-blue-400 space-y-1">
						<div>
							<strong>Super Admin:</strong> admin@beautyportal.com / admin123
						</div>
						<div>
							<strong>Salon Owner:</strong> owner@beautyportal.com / owner123
						</div>
						<div>
							<strong>Manager:</strong> manager@200below.com / manager123
						</div>
						<div>
							<strong>Staff:</strong> staff@beautyportal.com / staff123
						</div>
					</div>
				</div> */}
			</div>
		</div>
	);
};

export default Login;
